import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The chime, and the browser rules that decide whether anyone hears it.
 *
 * A browser cannot be made to beep on command. `play()` is refused on a
 * document the user has never interacted with, and no permission exists to
 * ask for — there is no `permissions.request({ name: 'autoplay' })`, and
 * Chrome's per-site Sound setting governs muting, not autoplay.
 *
 * What there IS, is a gesture. One click anywhere activates the DOCUMENT,
 * and every later `play()` in that page works — including across React
 * Router navigation, which never leaves the document. In a CRM that gesture
 * arrives within seconds of the tab opening.
 *
 * THE CASE THAT FAILS is a tab nobody touches: the CRM left open on a wall
 * display or a second monitor. `ready` is false there, and the caller shows
 * a one-time "click to enable" strip rather than failing silently.
 *
 * NO AUDIO FILE. The chime is synthesised — two short sine tones through the
 * WebAudio API — so there is no asset to ship, no request to fail, and no
 * format to be wrong about on one browser.
 */

/** Per browser, per device. A preference, not a setting worth a round trip. */
const MUTED_KEY = 'crm.alertSound.muted';
const CHOICE_KEY = 'crm.alertSound.choice';

/**
 * The sounds on offer, SYNTHESISED rather than shipped.
 *
 * Every one is a short list of sine tones, so there is no audio file to
 * download, no request to fail on a bad connection, and no codec to be
 * wrong about on one browser. Adding a sound is adding a row here.
 *
 * They are deliberately quiet and short. This fires next to somebody who
 * may be on a call, and a sound that makes a room look up is one the team
 * turns off within a day — at which point the reminders that mattered are
 * silent too.
 *
 * `gain` is per-sound because a low tone at the same amplitude reads as
 * much louder than a high one.
 */
export interface AlertTone {
  /** Hz. */
  hz: number;
  /** Seconds from the start of the sound. */
  at: number;
  /** Seconds. */
  length: number;
}

export interface AlertSoundOption {
  id: string;
  label: string;
  /** What it is for, in the chooser. */
  hint: string;
  gain: number;
  /**
   * The synthesised version. REQUIRED even on a sound that ships a file,
   * because it is the fallback — see `emit`.
   */
  tones: AlertTone[];
  /**
   * An audio file under `public/`, when this sound has one.
   *
   * Recorded audio has a richness sine tones cannot reach. The cost is a
   * fetch that can fail at the exact moment a reminder fires — a missing
   * file, a cold cache on a bad connection, a codec the browser refuses —
   * and a reminder that is silent because of a network blip is the failure
   * this whole feature exists to prevent. So the file is an UPGRADE on the
   * tones, never a replacement: if it will not play, the tones do.
   */
  src?: string;
}

export const ALERT_SOUNDS: readonly AlertSoundOption[] = [
  {
    /*
     * The default, and the only one that is a recording.
     *
     * Generated with ElevenLabs ("Airy social media chime, modern app
     * alert") and committed to `public/sounds/`. Two seconds, 33KB — long
     * for a notification but short enough not to outlast the glance it is
     * asking for.
     *
     * `tones` is a plain rising pair rather than anything clever: it exists
     * only for the moment the file does not load, and a fallback nobody has
     * heard is better than silence.
     */
    id: 'airy',
    label: 'Airy chime',
    hint: 'Soft and modern. The default.',
    gain: 0.09,
    src: '/sounds/airy-chime.mp3',
    tones: [
      { hz: 880, at: 0, length: 0.09 },
      { hz: 1318.5, at: 0.09, length: 0.11 },
    ],
  },
  {
    id: 'chime',
    label: 'Chime',
    hint: 'Two rising notes, synthesised.',
    gain: 0.09,
    tones: [
      { hz: 880, at: 0, length: 0.09 },
      { hz: 1174.7, at: 0.09, length: 0.09 },
    ],
  },
  {
    id: 'ping',
    label: 'Ping',
    hint: 'One short note. The least intrusive.',
    gain: 0.08,
    tones: [{ hz: 1318.5, at: 0, length: 0.11 }],
  },
  {
    id: 'marimba',
    label: 'Marimba',
    hint: 'Low and soft. Carries least in a quiet room.',
    gain: 0.12,
    tones: [
      { hz: 523.3, at: 0, length: 0.13 },
      { hz: 784, at: 0.1, length: 0.16 },
    ],
  },
  {
    id: 'alert',
    label: 'Alert',
    hint: 'Three quick notes. Hardest to miss.',
    gain: 0.09,
    tones: [
      { hz: 987.8, at: 0, length: 0.07 },
      { hz: 987.8, at: 0.1, length: 0.07 },
      { hz: 1318.5, at: 0.2, length: 0.12 },
    ],
  },
  {
    id: 'knock',
    label: 'Knock',
    hint: 'Two low taps. Reads as a notification, not an error.',
    gain: 0.14,
    tones: [
      { hz: 196, at: 0, length: 0.06 },
      { hz: 196, at: 0.11, length: 0.06 },
    ],
  },
];

const DEFAULT_SOUND = ALERT_SOUNDS[0];

const readChoice = (): AlertSoundOption => {
  try {
    const id = localStorage.getItem(CHOICE_KEY);
    return ALERT_SOUNDS.find((s) => s.id === id) ?? DEFAULT_SOUND;
  } catch {
    return DEFAULT_SOUND;
  }
};

const readMuted = (): boolean => {
  try {
    return localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    // Private windows and locked-down profiles throw on access. Audible is
    // the safer default: a missed reminder costs more than an unwanted beep.
    return false;
  }
};

export interface AlertSound {
  /** True once a gesture has unlocked audio AND the operator has not muted it. */
  ready: boolean;
  /** False until a gesture has unlocked audio, whatever the mute says. */
  unlocked: boolean;
  muted: boolean;
  setMuted: (next: boolean) => void;
  choice: AlertSoundOption;
  setChoice: (id: string) => void;
  /** Plays the chosen sound, or does nothing. Never throws. */
  play: () => void;
  /** Plays one by id and ignores the mute — for previewing in the chooser. */
  preview: (id: string) => void;
}

export function useAlertSound(): AlertSound {
  const context = useRef<AudioContext | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [muted, setMutedState] = useState(readMuted);
  const [choice, setChoiceState] = useState<AlertSoundOption>(readChoice);

  /*
   * Unlock on the first gesture of the session.
   *
   * `once: true` on all three, so the listeners remove themselves — this
   * runs at most once per document and then costs nothing. `pointerdown`
   * rather than `click` catches the gesture earlier, and `keydown` covers an
   * operator working entirely from the keyboard.
   */
  useEffect(() => {
    if (unlocked) return;

    const unlock = () => {
      try {
        const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        const ctx = context.current ?? new Ctor();
        context.current = ctx;
        // Created suspended when there has been no gesture; resuming inside
        // one is what actually performs the unlock.
        void ctx.resume();
        setUnlocked(true);
      } catch {
        // No WebAudio at all. The visual notification still happens.
      }
    };

    const opts = { once: true, passive: true } as const;
    window.addEventListener('pointerdown', unlock, opts);
    window.addEventListener('keydown', unlock, opts);
    window.addEventListener('touchstart', unlock, opts);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, [unlocked]);

  const setMuted = useCallback((next: boolean) => {
    setMutedState(next);
    try {
      localStorage.setItem(MUTED_KEY, next ? '1' : '0');
    } catch {
      // Not persisting a mute is survivable; it reverts next session.
    }
  }, []);

  /** The synthesised path, always available. */
  const emitTones = useCallback((sound: AlertSoundOption) => {
    const ctx = context.current;
    if (!ctx) return;
    try {
      const now = ctx.currentTime;
      for (const tone of sound.tones) {
        const at = now + tone.at;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = tone.hz;
        // An envelope, not a square on/off: an abrupt start and stop is the
        // click people hear as a glitch.
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(sound.gain, at + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + tone.length);
        osc.connect(gain).connect(ctx.destination);
        osc.start(at);
        osc.stop(at + tone.length + 0.02);
      }
    } catch {
      // Context closed, or the tab was discarded mid-play. Not worth a crash.
    }
  }, []);

  /**
   * Plays one option: the file if it has one, the tones otherwise — and the
   * tones anyway if the file will not play.
   *
   * `play()` returns a promise that REJECTS on a missing file, a refused
   * codec or a blocked autoplay, and a rejection nobody catches is both an
   * unhandled rejection in the console and a silent reminder. Catching it
   * and falling through is the whole reason `tones` stays required.
   */
  const emit = useCallback(
    (sound: AlertSoundOption) => {
      if (sound.src) {
        try {
          const audio = new Audio(sound.src);
          audio.volume = Math.min(1, sound.gain * 6);
          void audio.play().catch(() => emitTones(sound));
          return;
        } catch {
          // Fall through to the tones below.
        }
      }
      emitTones(sound);
    },
    [emitTones],
  );

  const play = useCallback(() => {
    if (muted) return;
    emit(choice);
  }, [muted, choice, emit]);

  /*
   * Previewing IGNORES the mute, because the only reason to press play on a
   * sound is to hear it. Muting is about reminders, not about the chooser.
   */
  const preview = useCallback((id: string) => emit(ALERT_SOUNDS.find((s) => s.id === id) ?? DEFAULT_SOUND), [emit]);

  const setChoice = useCallback((id: string) => {
    const next = ALERT_SOUNDS.find((s) => s.id === id) ?? DEFAULT_SOUND;
    setChoiceState(next);
    try {
      localStorage.setItem(CHOICE_KEY, next.id);
    } catch {
      // Not persisting reverts next session; the sound still changes now.
    }
  }, []);

  return { ready: unlocked && !muted, unlocked, muted, setMuted, choice, setChoice, play, preview };
}
